import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pagination } from "antd";

import ReactApexChart from "react-apexcharts";

import ViewMore from "../../assets/view-more.svg";

import InfoCard from "../../components/Common/InfoCard";
import LandLordTable from "../../components/Table/LandLordTable";
import ManagePropertiesChart from "../../components/LandLord/ManagePropertiesChart";

import { fetchOccupancyInsights } from "../../store/thunks/analyticsThunks";
import { fetchLandlords } from "../../store/thunks/userThunks";
import { getTenantPayments } from "../../store/thunks/paymentThunks";
import { useThunk } from "../../hooks/useThunk";
import Loader from "../../components/Common/Loader";

const Overview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromURL = parseInt(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(pageFromURL);
  const ITEMS_PER_PAGE = 10;

  const [landlordList, setLandlordList] = useState([]);
  const [tenantPayments, setTenantPayments] = useState([]);
  const [totalPayments, setTotalPayments] = useState(0);
  const [doFetchLandlords] = useThunk(fetchLandlords);
  const [doGetTenantPayments] = useThunk(getTenantPayments);

  const { data, isLoading } = useSelector((state) => state.analytics);

  const fetchData = useCallback(() => {

    dispatch(fetchOccupancyInsights());
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadTenantPayments = async () => {
    const offset = (currentPage - 1) * ITEMS_PER_PAGE;
    const res = await doGetTenantPayments({ offset, limit: ITEMS_PER_PAGE });
    if (res?.success) {
      setTenantPayments(res.response?.data?.data || []);
      setTotalPayments(res.response?.data?.pagination || 0);
    }
  };

  useEffect(() => {
    loadTenantPayments();
  }, [currentPage]);

  useEffect(() => {
    const getLandlords = async () => {
      const res = await doFetchLandlords({ page: 1, limit: 100 });
      if (res?.success) {
        const landlords = res.response?.data || [];
        setLandlordList(landlords);
      }
    };
    getLandlords();
  }, [dispatch]);

  useEffect(() => {
    const page = parseInt(searchParams.get("page")) || 1;
    setCurrentPage(page);
  }, [searchParams]);

  const forecastedData = Array.isArray(data?.forecastedData)
    ? data.forecastedData
    : [];

  const allOccupancyData = Array.isArray(data?.propertyData)
    ? data.propertyData
    : [];

  const allUnits = allOccupancyData.flatMap((p) => p.units || []);
  const occupiedUnits = allUnits.filter((u) => u.status === "OCCUPIED").length;
  // const vacantUnits = aggregated.totalUnits - occupiedUnits;
  const vacantUnits = allUnits.filter((u) => u.status === "VACANT").length;

  const paymentTableData = (tenantPayments || []).map((item, index) => ({
    key: `tenantPayment-${index}`,
    date: new Date(item.paymentDate).toLocaleDateString() + " " + new Date(item.paymentDate).toLocaleTimeString(),
    paymentDate: item.paymentDate,
    tenantName: item.tenantName,
    unitProperty: item.unitProperty.replace('@', '/'), // Replace "/" with "@" in Unit / Property field
    landlordName: item.landlordName,
    amountPaid: `UGX ${item.amount.toLocaleString()}`,
    amountLandloard: item.amount,
    platformCommission: 0, // Not provided in API response
    paymentMethod: item.paymentMethod,
    reference: item.transactionId,
    status: item.transactionStatus === "SUCCESS" ? "Paid" : item.transactionStatus,
    details: [{
      paymentMethod: item.paymentMethod,
      status: item.transactionStatus === "SUCCESS" ? "Paid" : item.transactionStatus,
      referenceId: item.transactionId,
      amount: item.amount,
      paymentDate: item.paymentDate
    }],
  }));

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSearchParams({ page: page.toString() });
  };

  const maxForecastValue = useMemo(() => {
    return Math.max(...forecastedData.map((item) => Number(item.value)), 1);
  }, [forecastedData]);

  const forecastedPercentageData = useMemo(() => {
    return forecastedData.map((item) => ({
      label: item.label,
      value: (Number(item.value) / maxForecastValue) * 100,
    }));
  }, [forecastedData, maxForecastValue]);

  const forecastedIncomeChartOptions = {
    chart: {
      type: "area",
      height: 400,
      toolbar: { show: false },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        gradientToColors: ["rgba(146, 80, 229, 0.25)"],
        stops: [0, 100],
        opacityFrom: 1,
        opacityTo: 1,
      },
    },
    xaxis: {
      categories: forecastedData.map((item) => item.label),
      title: {
        text: "Months",
        style: { fontSize: "14px", fontWeight: 600 },
      },
      labels: {
        style: { fontSize: "12px", fontWeight: 700, colors: "#00000099" },
        formatter: (val) =>
          (/^(Pre|Post)-/.test(val) ? "" : val),
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      title: {
        text: "Income",
        style: { fontSize: "14px", fontWeight: 600 },
      },
    },
    tooltip: {
      y: { formatter: (val) => `${val}%` },
    },
    colors: ["#9250E5"],
  };

  const forecastedIncomeChartSeries = [
    {
      name: "Income",
      data: forecastedPercentageData.map((item) =>
        parseFloat(item.value.toFixed(0))
      ),
    },
  ];

  const tableColumns = [
    { title: "Date / Time", dataIndex: "date", key: "date" },
    { title: "Tenant Name", dataIndex: "tenantName", key: "tenantName" },
    {
      title: "Unit / Property",
      dataIndex: "unitProperty",
      key: "unitProperty",
    },
    { title: "Landlord Name", dataIndex: "landlordName", key: "landlordName" },
    {
      title: "Amount Paid",
      dataIndex: "amountPaid",
      key: "amountPaid",
      render: (amount) => <span className="font-n">{amount}</span>,
    },
  ];

  const subTableConfig = {
    dataField: "details",
    columns: [
      {
        title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Payment Method</span>,
        dataIndex: "paymentMethod",
        key: "paymentMethod",
      },
      {
        title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Status</span>,
        dataIndex: "status",
        key: "status",
        render: (status) => (
          <span
            className={`text-[8px] sm:text-[10px] md:text-[11px] lg:text-[12px] px-2 py-1 rounded font-medium ${ status === "Paid"
                    ? "bg-[#DCFCE7] text-[#3fab67]"
                    : status === "PROCESSING"
                        ? "bg-[#E0F4FF] text-[#0078D4]"
                        : status === "FAILED"
                        ? "bg-[#FEE2E2] text-[#EF4444]"
                        : "bg-[#F1F5F9] text-[#9E9E9E]"
              }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
          </span>
        ),
      },
      {
        title: <span className="text-[#4772F1] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">Reference ID</span>,
        dataIndex: "referenceId",
        key: "referenceId",
      },
    ],
  };

  if (isLoading)
    return <div className="text-center py-20"><Loader /></div>;

  return (
    <div className="min-h-screen p-5 sm:p-6 md:p-[30px]">
      <div className="mb-[0px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[0px]">
        <div className="grid gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[52px] gap-y-[30px] $ grid-cols-2 xl:grid-cols-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <InfoCard
              label="Total Landlords"
              value={landlordList.length}
              className=" max-h-[120px] mx-auto"
              labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[24px] sm:text-[30px] md:text-[40px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Total Properties"
              value={allOccupancyData.length}
              className=" max-h-[120px] mx-auto"
              labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[24px] sm:text-[30px] md:text-[40px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Occupied Units"
              value={occupiedUnits}
              className=" max-h-[120px] mx-auto"
              labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] mb-5 leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              valueStyle="font-inter font-semibold text-[24px] sm:text-[30px] md:text-[40px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Vacant Units"
              value={vacantUnits}
              className=" max-h-[120px] mx-auto"
              labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] leading-[100% mb-5 bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              valueStyle="font-inter font-semibold text-[24px] sm:text-[30px] md:text-[40px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid gap-4 sm:gap-4 md:gap-4 xl:gap-[30px] grid-cols-1 md:grid-cols-2 mt-[30px]">
        <div className="bg-white p-4 sm:p-5 md:p-6 lg:p-[30px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F]">
          <h2 className="text-[22px] font-semibold text-[#4772F1] mb-4 sm:mb-[24px] md:mb-[36px] lg:mb-[44px]">
            Revenue Actual Forecast
          </h2>
          <ReactApexChart
            options={forecastedIncomeChartOptions}
            series={forecastedIncomeChartSeries}
            type="area"
            height={300}
          />
        </div>
        <div className="bg-white p-4 sm:p-5 md:p-6 lg:p-[30px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F]">
          <h2 className="text-[22px] font-semibold text-[#4772F1] mb-4 sm:mb-[24px] md:mb-[40px] lg:mb-[60px]">
            Manage Properties
          </h2>
          <ManagePropertiesChart data={{ occupied: occupiedUnits, vacant: vacantUnits }} />
        </div>
      </div>

      {/* Tenant Payment Table */}
      <div className="bg-white rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px] p-4 sm:p-5 md:p-6 lg:p-[30px]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-[16px] sm:text-[18px] md:text-[20px] lg:text-[22px] font-semibold text-[#4772F1]">
            Tenant Payment Panel
          </h2>
          <button
            onClick={() => navigate("/landlords/tenant-payment-activity")}
            className="text-[#3B82F6] text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px] font-normal  flex items-center"
          >
            View More
            <img
              src={ViewMore}
              alt="View More"
              className="w-[8px] sm:w-[9px] md:w-[11px] lg:w-[12px] h-[8px] sm:h-[9px] md:h-[11px] lg:h-[12px] mt-[1px] ml-[6px]"
            />
          </button>
        </div>
        <div className="overflow-x-auto border-[1px] border-[#E5E5E5] rounded-[8px]">
          <LandLordTable
            dataSource={paymentTableData}
            columns={tableColumns}
            hasSubTable={true}
            subTableConfig={subTableConfig}
            rowKey="key"
          />
        </div>
        {/* <div className="mt-4 flex justify-center">
          <Pagination
            current={currentPage}
            total={totalPayments.totalDocuments}
            pageSize={ITEMS_PER_PAGE}
            onChange={handlePageChange}
            className="custom-pagination"
            showSizeChanger={false}
            hideOnSinglePage={true}
          />
        </div> */}
      </div>
    </div>
  );
};

export default Overview;
